import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { TrendChart } from '../components/TrendChart'
import { summarizeAdvertisingProfit } from '../advertising'
import { useProductData } from '../data'
import { jdProfitAfterAfterSales, summarizeJdAfterSalesLoss } from '../jdAfterSalesProfit'
import { useJdAfterSalesFacts } from '../platform'
import {
  accessoryFor,
  estimatedProfitForSku,
  internalProductCost,
  JD_STORE_ID,
  productMasterCostDefaults,
  productMasterRecordMap,
  profitWithRebateFor,
  rebateAmountForSku,
  shippingProviderFor,
} from '../productMaster'
import { skuMatchesTag } from '../segments'
import { statisticsPeriodOptions, statisticsWindow, type StatisticsPeriod, visibleDateRange } from '../statistics'
import {
  deltaRate,
  integer,
  isActive,
  metricsAcrossDates,
  metricsOn,
  money,
  percent,
  previousDateOf,
  sumMetrics,
} from '../utils'

type OverviewTrendMetric =
  | 'gmv'
  | 'profit'
  | 'profitWithRebate'
  | 'profitWithRebateMargin'
  | 'profitAfterAfterSales'
  | 'advertisingSpend'
  | 'profitAfterAdvertising'
  | 'profitAfterAdvertisingMargin'
  | 'activeSpus'
  | 'activeSkus'
  | 'soldItems'
  | 'productVisitors'
  | 'conversionRate'
  | 'averageOrderValue'
  | 'cancelRefundAmount'

const OVERVIEW_TREND_OPTIONS: Record<OverviewTrendMetric, {
  title: string
  unit: string
  valueLabel: string
  ariaLabel: string
  formatter: (value: number) => string
}> = {
  gmv: {
    title: '30天成交金额趋势',
    unit: '金额（元）',
    valueLabel: '成交金额',
    ariaLabel: '近30天店铺商品成交金额趋势',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profit: {
    title: '30天预估利润趋势',
    unit: '金额（元）',
    valueLabel: '预估利润',
    ariaLabel: '近30天店铺商品预估利润趋势',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitWithRebate: {
    title: '30天含返点利润趋势',
    unit: '金额（元）',
    valueLabel: '含返点利润',
    ariaLabel: '近30天店铺商品含返点利润趋势',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitWithRebateMargin: {
    title: '30天含返点利润率趋势',
    unit: '利润率（%）',
    valueLabel: '含返点利润率',
    ariaLabel: '近30天店铺商品含返点利润率趋势',
    formatter: (value) => percent(value * 100),
  },
  profitAfterAfterSales: {
    title: '30天扣售后利润趋势',
    unit: '金额（元）',
    valueLabel: '扣售后利润',
    ariaLabel: '近30天店铺扣售后利润趋势',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  advertisingSpend: {
    title: '30天广告费趋势',
    unit: '金额（元）',
    valueLabel: '广告费',
    ariaLabel: '近30天店铺广告费用趋势',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitAfterAdvertising: {
    title: '30天广告后利润趋势',
    unit: '金额（元）',
    valueLabel: '广告后利润',
    ariaLabel: '近30天店铺广告后利润趋势',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitAfterAdvertisingMargin: {
    title: '30天广告后利润率趋势',
    unit: '利润率（%）',
    valueLabel: '广告后利润率',
    ariaLabel: '近30天店铺广告后利润率趋势',
    formatter: (value) => percent(value * 100),
  },
  activeSpus: {
    title: '30天动销SPU趋势',
    unit: 'SPU（个）',
    valueLabel: '动销SPU',
    ariaLabel: '近30天店铺动销SPU数量趋势',
    formatter: (value) => integer(value),
  },
  activeSkus: {
    title: '30天动销SKU趋势',
    unit: 'SKU（个）',
    valueLabel: '动销SKU',
    ariaLabel: '近30天店铺动销SKU数量趋势',
    formatter: (value) => integer(value),
  },
  soldItems: {
    title: '30天成交件数趋势',
    unit: '件数（件）',
    valueLabel: '成交件数',
    ariaLabel: '近30天店铺商品成交件数趋势',
    formatter: (value) => integer(value),
  },
  productVisitors: {
    title: '30天商品访客趋势',
    unit: '访客（人）',
    valueLabel: '商品访客',
    ariaLabel: '近30天店铺商品访客趋势',
    formatter: (value) => integer(value),
  },
  conversionRate: {
    title: '30天成交转化率趋势',
    unit: '转化率（%）',
    valueLabel: '成交转化率',
    ariaLabel: '近30天店铺商品成交转化率趋势',
    formatter: (value) => percent(value),
  },
  averageOrderValue: {
    title: '30天客单价趋势',
    unit: '金额（元）',
    valueLabel: '客单价',
    ariaLabel: '近30天店铺商品客单价趋势',
    formatter: (value) => money(value),
  },
  cancelRefundAmount: {
    title: '30天取消退款金额趋势',
    unit: '金额（元）',
    valueLabel: '取消退款金额',
    ariaLabel: '近30天店铺取消退款金额趋势',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
}

export function OverviewPage() {
  const { advertisingCosts, data, internalProductInfo, productMaster, segmentIndex, selectedDate } = useProductData()
  const afterSalesFacts = useJdAfterSalesFacts()
  const [statisticsPeriod, setStatisticsPeriod] = useState<StatisticsPeriod>('day')
  const [trendMetric, setTrendMetric] = useState<OverviewTrendMetric>('gmv')
  const productMasterRecords = useMemo(
    () => productMasterRecordMap(productMaster, JD_STORE_ID),
    [productMaster],
  )
  const costDefaults = useMemo(
    () => productMasterCostDefaults(productMaster, JD_STORE_ID),
    [productMaster],
  )
  const internalRecordsByCode = useMemo(() => {
    const index = new Map<string, NonNullable<typeof internalProductInfo>['records']>()
    for (const record of internalProductInfo?.records || []) {
      if (!record.productCode) continue
      const matches = index.get(record.productCode) || []
      matches.push(record)
      index.set(record.productCode, matches)
    }
    return index
  }, [internalProductInfo])
  const period = useMemo(
    () => data ? statisticsWindow(statisticsPeriod, selectedDate, data.meta.dates) : null,
    [data, selectedDate, statisticsPeriod],
  )
  const view = useMemo(() => {
    if (!data || !period) return null
    const spus = data.products.filter((product) => product.level === 'spu')
    const skus = data.products.filter((product) => product.level === 'sku')
    const previousDate = previousDateOf(data.meta.dates, selectedDate)
    const spuRows = spus.map((product) => ({
      product,
      metrics: metricsAcrossDates(product, period.dates),
    }))
    const skuRows = skus.map((product) => ({
      product,
      metrics: metricsAcrossDates(product, period.dates),
    }))
    const spuMetrics = spuRows.map((item) => item.metrics)
    const storeMetrics = sumMetrics(spuMetrics, period.label)
    const totalGmv = storeMetrics.gmv
    const soldItems = storeMetrics.soldItemCount
    const activeSpus = spuMetrics.filter(isActive).length
    const activeSkus = skuRows.filter((item) => isActive(item.metrics)).length
    const profitSources = skus.map((product) => {
      const record = productMasterRecords.get(product.id)
      const companyRecords = internalRecordsByCode.get(product.merchantSku.trim()) || []
      const isAccessory = accessoryFor(product, record, companyRecords)
      const internalCost = internalProductCost(companyRecords)
      const inheritedJdWarehouse = skuMatchesTag(segmentIndex, product.id, 'jd-warehouse', period.dates)
      const shippingProvider = shippingProviderFor(
        record,
        inheritedJdWarehouse,
        selectedDate,
        costDefaults,
        isAccessory,
        internalCost.isBrandSpecial,
      )
      return { product, record, companyRecords, isAccessory, shippingProvider }
    })
    const profitRows = profitSources.map((source) => {
      const estimate = estimatedProfitForSku(
        source.product,
        source.record,
        period.dates,
        costDefaults,
        source.companyRecords,
        source.shippingProvider,
      )
      const rebateAmount = rebateAmountForSku(
        source.product,
        period.dates,
        source.companyRecords,
        source.isAccessory,
      )
      return {
        product: source.product,
        estimate,
        profitWithRebate: profitWithRebateFor(estimate.profit, rebateAmount),
      }
    })
    const activeProfitRows = profitRows.filter((item) => item.estimate.activeDates > 0)
    const coveredProfitRows = activeProfitRows.filter((item) =>
      item.estimate.profit !== null && item.profitWithRebate !== null,
    )
    const totalProfit = coveredProfitRows.reduce((sum, item) => sum + (item.estimate.profit || 0), 0)
    const totalProfitWithRebate = coveredProfitRows.reduce(
      (sum, item) => sum + (item.profitWithRebate || 0),
      0,
    )
    const coveredProfitGmv = coveredProfitRows.reduce(
      (sum, item) => sum + item.estimate.metrics.gmv,
      0,
    )
    const totalProfitWithRebateMargin = coveredProfitGmv > 0
      ? totalProfitWithRebate / coveredProfitGmv
      : null
    const afterSalesDeduction = summarizeJdAfterSalesLoss(
      afterSalesFacts.data || null,
      period.dates,
      skus,
      productMasterRecords,
      internalRecordsByCode,
      costDefaults,
    )
    const profitAfterAfterSales = jdProfitAfterAfterSales(
      coveredProfitRows.length ? totalProfitWithRebate : null,
      afterSalesDeduction,
    )
    const advertisingSummary = summarizeAdvertisingProfit(
      advertisingCosts,
      period.dates,
      totalGmv,
      profitAfterAfterSales,
    )
    const spuProfitSummaries = new Map<string, {
      activeSkuCount: number
      coveredSkuCount: number
      profitWithRebate: number
      coveredGmv: number
    }>()
    for (const item of profitRows) {
      const spuId = item.product.spuId
      if (!spuId || item.estimate.activeDates === 0) continue
      const summary = spuProfitSummaries.get(spuId) || {
        activeSkuCount: 0,
        coveredSkuCount: 0,
        profitWithRebate: 0,
        coveredGmv: 0,
      }
      summary.activeSkuCount += 1
      if (item.estimate.profit !== null && item.profitWithRebate !== null) {
        summary.coveredSkuCount += 1
        summary.profitWithRebate += item.profitWithRebate
        summary.coveredGmv += item.estimate.metrics.gmv
      }
      spuProfitSummaries.set(spuId, summary)
    }
    const profitBySpuId = new Map<string, number | null>()
    const profitMarginBySpuId = new Map<string, number | null>()
    const profitContextBySpuId = new Map<string, string>()
    for (const spu of spus) {
      const summary = spuProfitSummaries.get(spu.id)
      if (!summary) {
        profitBySpuId.set(spu.id, null)
        profitMarginBySpuId.set(spu.id, null)
        profitContextBySpuId.set(spu.id, '当前范围无可计算成交SKU')
        continue
      }
      profitBySpuId.set(
        spu.id,
        summary.coveredSkuCount > 0 ? summary.profitWithRebate : null,
      )
      profitMarginBySpuId.set(
        spu.id,
        summary.coveredGmv > 0 ? summary.profitWithRebate / summary.coveredGmv : null,
      )
      profitContextBySpuId.set(
        spu.id,
        `已覆盖 ${summary.coveredSkuCount}/${summary.activeSkuCount} 个成交SKU`,
      )
    }
    const previousGmv = spus.reduce((sum, product) => sum + metricsOn(product, previousDate).gmv, 0)
    const gmvRate = deltaRate(totalGmv, previousGmv)
    const trendDates = statisticsWindow('last30', selectedDate, data.meta.dates).dates
    const trend = trendDates.map((date) => {
      const dailySpuMetrics = spus.map((product) => metricsOn(product, date))
      const dailySkuMetrics = skus.map((product) => metricsOn(product, date))
      const dailyProfitRows = profitSources.map((source) => {
        const estimate = estimatedProfitForSku(
          source.product,
          source.record,
          [date],
          costDefaults,
          source.companyRecords,
          source.shippingProvider,
        )
        const rebateAmount = rebateAmountForSku(
          source.product,
          [date],
          source.companyRecords,
          source.isAccessory,
        )
        return {
          estimate,
          profitWithRebate: profitWithRebateFor(estimate.profit, rebateAmount),
        }
      })
      const coveredDailyProfitRows = dailyProfitRows.filter((item) =>
        item.estimate.activeDates > 0
        && item.estimate.profit !== null
        && item.profitWithRebate !== null,
      )
      const dailyProfitWithRebate = coveredDailyProfitRows.reduce(
        (sum, item) => sum + (item.profitWithRebate || 0),
        0,
      )
      const dailyProfitBeforeAdvertising = coveredDailyProfitRows.length
        ? dailyProfitWithRebate
        : null
      const dailyCoveredGmv = coveredDailyProfitRows.reduce(
        (sum, item) => sum + item.estimate.metrics.gmv,
        0,
      )
      const dailyGmv = dailySpuMetrics.reduce((sum, metrics) => sum + metrics.gmv, 0)
      const dailyStoreMetrics = sumMetrics(dailySpuMetrics, date)
      const dailyAfterSales = summarizeJdAfterSalesLoss(
        afterSalesFacts.data || null,
        [date],
        skus,
        productMasterRecords,
        internalRecordsByCode,
        costDefaults,
      )
      const dailyProfitAfterAfterSales = jdProfitAfterAfterSales(dailyProfitBeforeAdvertising, dailyAfterSales)
      const dailyAdvertising = summarizeAdvertisingProfit(
        advertisingCosts,
        [date],
        dailyGmv,
        dailyProfitAfterAfterSales,
      )
      return {
        date,
        gmv: dailyGmv,
        profit: coveredDailyProfitRows.reduce((sum, item) => sum + (item.estimate.profit || 0), 0),
        profitWithRebate: dailyProfitWithRebate,
        profitWithRebateMargin: dailyCoveredGmv > 0 ? dailyProfitWithRebate / dailyCoveredGmv : 0,
        profitAfterAfterSales: dailyProfitAfterAfterSales,
        advertisingSpend: dailyAdvertising.complete ? dailyAdvertising.total : null,
        profitAfterAdvertising: dailyAdvertising.profitAfterAdvertising,
        profitAfterAdvertisingMargin: dailyAdvertising.profitAfterAdvertisingMargin,
        advertisingComplete: dailyAdvertising.complete,
        activeSpus: dailySpuMetrics.filter(isActive).length,
        activeSkus: dailySkuMetrics.filter(isActive).length,
        soldItems: dailyStoreMetrics.soldItemCount,
        productVisitors: dailyStoreMetrics.productVisitors,
        conversionRate: dailyStoreMetrics.conversionRate,
        averageOrderValue: dailyStoreMetrics.averageOrderValue,
        cancelRefundAmount: dailyStoreMetrics.cancelRefundAmount,
      }
    })
    const focusRows = statisticsPeriod === 'day'
      ? (() => {
          const changes = spuRows.map((item) => ({
            product: item.product,
            delta: item.metrics.gmv - metricsOn(item.product, previousDate).gmv,
            metrics: item.metrics,
          })).filter((item) => item.delta !== 0)
          const growth = changes.slice().sort((a, b) => b.delta - a.delta).slice(0, 4)
          const decline = changes.slice().sort((a, b) => a.delta - b.delta).slice(0, 4)
          return [...growth, ...decline]
        })()
      : spuRows
        .filter((item) => isActive(item.metrics))
        .sort((a, b) => b.metrics.gmv - a.metrics.gmv)
        .slice(0, 8)
        .map((item) => ({ ...item, delta: item.metrics.gmv }))
    const focus = focusRows.map((item) => item.product)
    const focusValueById = new Map(focusRows.map((item) => [item.product.id, item.delta]))
    const warehouseSkus = skus.filter((product) =>
      skuMatchesTag(segmentIndex, product.id, 'jd-warehouse', period.dates),
    )
    const warehouseSkuIds = new Set(warehouseSkus.map((product) => product.id))
    const warehouseActiveProfitRows = activeProfitRows.filter((item) => warehouseSkuIds.has(item.product.id))
    const warehouseCoveredProfitRows = coveredProfitRows.filter((item) => warehouseSkuIds.has(item.product.id))
    const warehouseProfitWithRebate = warehouseCoveredProfitRows.reduce(
      (sum, item) => sum + (item.profitWithRebate || 0),
      0,
    )
    const warehouseCoveredProfitGmv = warehouseCoveredProfitRows.reduce(
      (sum, item) => sum + item.estimate.metrics.gmv,
      0,
    )
    const warehouseCurrentRows = warehouseSkus.map((product) => ({
      product,
      metrics: metricsAcrossDates(product, period.dates),
    }))
    const warehouseMetrics = sumMetrics(warehouseCurrentRows.map((item) => item.metrics), period.label)
    const warehousePrevious = sumMetrics(
      warehouseSkus.map((product) => metricsOn(product, previousDate)),
      previousDate,
    )
    const warehouseSpuIds = new Set(warehouseSkus.map((product) => product.spuId).filter(Boolean))
    const warehouseActiveSpuIds = new Set(
      warehouseCurrentRows.filter((item) => isActive(item.metrics)).map((item) => item.product.spuId).filter(Boolean),
    )
    const warehouse = {
      metrics: warehouseMetrics,
      gmvRate: deltaRate(warehouseMetrics.gmv, warehousePrevious.gmv),
      spuCount: warehouseSpuIds.size,
      skuCount: warehouseSkus.length,
      activeSpuCount: warehouseActiveSpuIds.size,
      activeSkuCount: warehouseCurrentRows.filter((item) => isActive(item.metrics)).length,
    }
    return {
      totalGmv,
      soldItems,
      productVisitors: storeMetrics.productVisitors,
      conversionRate: storeMetrics.conversionRate,
      averageOrderValue: storeMetrics.averageOrderValue,
      cancelRefundAmount: storeMetrics.cancelRefundAmount,
      activeSpus,
      activeSkus,
      totalProfit,
      totalProfitWithRebate,
      totalProfitWithRebateMargin,
      afterSalesDeduction,
      profitAfterAfterSales,
      advertisingSummary,
      activeProfitSkuCount: activeProfitRows.length,
      coveredProfitSkuCount: coveredProfitRows.length,
      profitBySpuId,
      profitMarginBySpuId,
      profitContextBySpuId,
      gmvRate,
      trend,
      focus,
      focusValueById,
      warehouse,
      warehouseProfit: {
        profitWithRebate: warehouseProfitWithRebate,
        profitWithRebateMargin: warehouseCoveredProfitGmv > 0
          ? warehouseProfitWithRebate / warehouseCoveredProfitGmv
          : null,
        activeSkuCount: warehouseActiveProfitRows.length,
        coveredSkuCount: warehouseCoveredProfitRows.length,
      },
    }
  }, [advertisingCosts, afterSalesFacts.data, costDefaults, data, internalRecordsByCode, period, productMasterRecords, segmentIndex, selectedDate, statisticsPeriod])

  if (!data || !period || !view) return null
  const showDayComparison = statisticsPeriod === 'day'
  const periodRange = visibleDateRange(period.dates)
  const aggregateContext = showDayComparison ? undefined : periodRange
  const profitContext = view.activeProfitSkuCount === 0
    ? '当前范围无成交SKU'
    : `已覆盖 ${view.coveredProfitSkuCount}/${view.activeProfitSkuCount} 个成交SKU`
  const profitAvailable = view.activeProfitSkuCount === 0 || view.coveredProfitSkuCount > 0
  const trendOption = OVERVIEW_TREND_OPTIONS[trendMetric]
  const trendData = view.trend
    .filter((point) => typeof point[trendMetric] === 'number')
    .map((point) => ({ date: point.date, value: point[trendMetric] as number }))
  return (
    <div className="overview-shell">
      <section className="overview-section">
        <div className="section-heading-row overview-period-heading">
          <div>
            <h1>经营概览</h1>
            <p>{period.label} · {periodRange} · {period.complete ? `完整${period.requestedDayCount}天` : `仅覆盖${period.dates.length}/${period.requestedDayCount}天`}</p>
          </div>
          <div className="page-title-actions">
            <DataFreshnessBadge date={data.meta.maxDate} label="商品数据截至" />
            <div className="metric-switch" role="group" aria-label="首页统计时间范围">
              {statisticsPeriodOptions.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={item.key === statisticsPeriod ? 'is-active' : ''}
                  aria-pressed={item.key === statisticsPeriod}
                  onClick={() => setStatisticsPeriod(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {!period.complete && (
          <p className="period-coverage-warning">
            请求范围为 {period.startDate} 至 {period.endDate}，当前滚动数据只能覆盖 {periodRange}；以下为部分区间汇总。
          </p>
        )}
        <MetricStrip items={[
          {
            label: '成交金额',
            value: money(view.totalGmv),
            context: showDayComparison
              ? (view.gmvRate === null ? '较前日新增' : `较前日 ${view.gmvRate >= 0 ? '+' : ''}${view.gmvRate.toFixed(2)}%`)
              : aggregateContext,
            active: trendMetric === 'gmv',
            onClick: () => setTrendMetric('gmv'),
          },
          {
            label: '成交件数',
            value: integer(view.soldItems),
            context: aggregateContext,
            active: trendMetric === 'soldItems',
            onClick: () => setTrendMetric('soldItems'),
          },
          {
            label: '商品访客',
            value: integer(view.productVisitors),
            context: '按SPU分天汇总',
            active: trendMetric === 'productVisitors',
            onClick: () => setTrendMetric('productVisitors'),
          },
          {
            label: '成交转化率',
            value: percent(view.conversionRate),
            context: '成交客户 ÷ 商品访客',
            active: trendMetric === 'conversionRate',
            onClick: () => setTrendMetric('conversionRate'),
          },
          {
            label: '客单价',
            value: money(view.averageOrderValue),
            context: '成交金额 ÷ 成交客户',
            active: trendMetric === 'averageOrderValue',
            onClick: () => setTrendMetric('averageOrderValue'),
          },
          {
            label: '动销SKU',
            value: integer(view.activeSkus),
            context: '当前范围有成交',
            active: trendMetric === 'activeSkus',
            onClick: () => setTrendMetric('activeSkus'),
          },
        ]} />
      </section>

      <section className="overview-section overview-profit-snapshot">
        <div className="section-heading-row">
          <div>
            <h2>利润快照</h2>
            <p>项目差异化口径 · 商品成本、返点和店铺级广告费用</p>
          </div>
          <Link className="text-link" to="/profit">进入利润中心</Link>
        </div>
        <MetricStrip items={[
          {
            label: '含返点利润（广告前）',
            value: profitAvailable ? money(view.totalProfitWithRebate) : '—',
            context: profitContext,
            active: trendMetric === 'profitWithRebate',
            onClick: () => setTrendMetric('profitWithRebate'),
          },
          {
            label: '扣售后利润',
            value: view.profitAfterAfterSales === null ? '—' : money(view.profitAfterAfterSales),
            context: view.afterSalesDeduction.complete
              ? `售后损失 ${money(view.afterSalesDeduction.totalLoss)} · 二退 ${view.afterSalesDeduction.secondReturnOrderCount}单`
              : `售后数据覆盖 ${view.afterSalesDeduction.coveredDays}/${view.afterSalesDeduction.requestedDays} 天`,
            active: trendMetric === 'profitAfterAfterSales',
            onClick: () => setTrendMetric('profitAfterAfterSales'),
          },
          {
            label: '广告费',
            value: view.advertisingSummary.complete ? money(view.advertisingSummary.total) : '—',
            context: view.advertisingSummary.complete
              ? `${view.advertisingSummary.provisionalDays}天含暂估值`
              : `覆盖 ${view.advertisingSummary.coveredDays}/${view.advertisingSummary.requestedDays} 天`,
            active: trendMetric === 'advertisingSpend',
            onClick: () => setTrendMetric('advertisingSpend'),
          },
          {
            label: '广告后利润',
            value: view.advertisingSummary.profitAfterAdvertising === null
              ? '—'
              : money(view.advertisingSummary.profitAfterAdvertising),
            context: view.advertisingSummary.complete ? '扣售后利润 − 广告费' : '广告或售后日期覆盖不足',
            active: trendMetric === 'profitAfterAdvertising',
            onClick: () => setTrendMetric('profitAfterAdvertising'),
          },
          {
            label: '含返点利润率',
            value: view.totalProfitWithRebateMargin === null
              ? '—'
              : percent(view.totalProfitWithRebateMargin * 100),
            context: '含返点利润 ÷ 已覆盖成交金额',
            active: trendMetric === 'profitWithRebateMargin',
            onClick: () => setTrendMetric('profitWithRebateMargin'),
          },
          {
            label: '广告后利润率',
            value: view.advertisingSummary.profitAfterAdvertisingMargin === null
              ? '—'
              : percent(view.advertisingSummary.profitAfterAdvertisingMargin * 100),
            context: '广告后利润 ÷ 成交金额',
            active: trendMetric === 'profitAfterAdvertisingMargin',
            onClick: () => setTrendMetric('profitAfterAdvertisingMargin'),
          },
          {
            label: '成本覆盖',
            value: `${view.coveredProfitSkuCount}/${view.activeProfitSkuCount}`,
            context: '已覆盖成交SKU',
          },
        ]} />
      </section>

      <div className="overview-insight-grid">
        <section className="overview-section chart-section">
          <div className="section-heading-row"><h2>{trendOption.title}</h2><span>{trendOption.unit}</span></div>
          <TrendChart
            data={trendData}
            valueFormatter={trendOption.formatter}
            valueLabel={trendOption.valueLabel}
            ariaLabel={trendOption.ariaLabel}
          />
        </section>
        <aside className="overview-section overview-anomaly-panel">
          <div className="section-heading-row">
            <div>
              <h2>{showDayComparison ? '当前重点' : '成交重点'}</h2>
              <p>{showDayComparison ? '较前日成交金额异动' : `${period.label}成交金额`}</p>
            </div>
          </div>
          <div className="overview-anomaly-list">
            {view.focus.slice(0, 6).map((product) => {
              const value = view.focusValueById.get(product.id) || 0
              return (
                <Link key={product.id} to={`/product/spu/${product.id}`}>
                  {product.image
                    ? <img src={product.image} alt="" loading="lazy" />
                    : <span className="overview-anomaly-image">无图</span>}
                  <span title={product.nameNote ? product.name : undefined}>{product.nameNote || product.name}</span>
                  <strong className={showDayComparison ? value >= 0 ? 'is-positive' : 'is-negative' : ''}>
                    {showDayComparison && value > 0 ? '+' : showDayComparison && value < 0 ? '−' : ''}
                    {money(Math.abs(value))}
                  </strong>
                </Link>
              )
            })}
          </div>
        </aside>
      </div>

      <section className="overview-section overview-entry-section">
        <div className="section-heading-row">
          <div>
            <h2>深入查看</h2>
            <p>首页只保留判断入口，完整表格和证据通过下钻查看</p>
          </div>
        </div>
        <div className="overview-entry-links">
          <Link to="/profit"><strong>利润中心</strong><span>利润结果、趋势、贡献、风险和成本覆盖</span><b>查看</b></Link>
          <Link to="/analysis"><strong>机会与风险</strong><span>流量、广告效率、四象限、退款和执行清单</span><b>查看</b></Link>
          <Link to={`/spu?fulfillment=jd-warehouse&period=${statisticsPeriod}`}><strong>入仓款商品</strong><span>查看京仓发货SPU及完整经营指标</span><b>查看</b></Link>
        </div>
      </section>
    </div>
  )
}
